import { PoolConfig } from '../types/pool';
import liquidityPoolsData from './liquidity_pools.json';

// Enhanced interface for the new JSON structure
interface PoolData {
  address: string;
  feeTier: number;
  description: string;
  scanUrl: string;
  poolUrl: string;
  tokens: string[];
}

type DEXType = 'uniswap_v3' | 'pancakeswap_v3' | 'uniswap_v4' | 'sushiswap_v3';

interface LiquidityPoolsData {
  pools: {
    [dex in DEXType]: {
      [poolName: string]: PoolData;
    };
  };
  dexInfo: {
    [dex in DEXType]: {
      name: string;
      color: string;
      subgraphId: string;
    };
  };
}

// Load pool configurations from the enhanced liquidity_pools.json
// This ensures both frontend and core_analysis use the same pool data
const loadPoolConfigs = (): PoolConfig[] => {
  const pools: PoolConfig[] = [];
  const data = liquidityPoolsData as LiquidityPoolsData;
  
  // Process all DEXes dynamically
  Object.entries(data.pools).forEach(([dex, dexPools]) => {
    Object.entries(dexPools).forEach(([name, poolData]) => {
      pools.push({
        address: poolData.address,
        name,
        dex: dex as DEXType,
        feeTier: poolData.feeTier,
        description: poolData.description,
        scanUrl: poolData.scanUrl,
        poolUrl: poolData.poolUrl
      });
    });
  });
  
  return pools;
};

export const POOL_CONFIGS: PoolConfig[] = loadPoolConfigs();

// Helper functions
export const getPoolsByDEX = (dex: string): PoolConfig[] => 
  POOL_CONFIGS.filter(pool => pool.dex === dex);

export const getPoolByAddress = (address: string): PoolConfig | undefined => 
  POOL_CONFIGS.find(pool => pool.address.toLowerCase() === address.toLowerCase());

export const getAllPoolAddresses = (): string[] => 
  POOL_CONFIGS.map(pool => pool.address);

export const getPoolsByTokenPair = (token0: string, token1: string): PoolConfig[] =>
  POOL_CONFIGS.filter(pool => 
    pool.name.toLowerCase().includes(token0.toLowerCase()) && 
    pool.name.toLowerCase().includes(token1.toLowerCase())
  );

// DEX information loaded from JSON
export const DEX_INFO = (liquidityPoolsData as LiquidityPoolsData).dexInfo;

// EverValue Brand-Compliant Stacked Chart Color Palette
// Designed for optimal visual layering and brand consistency
export const POOL_COLOR_PALETTE = [
  // Primary EverValue Orange Spectrum (most prominent)
  '#FF8C00',  // Primary brand orange
  '#FF9500',  // Warm orange
  '#FFA500',  // Bright orange
  
  // Complementary Warm Spectrum (harmonious with orange)
  '#FFB347',  // Peach orange
  '#FF7F50',  // Coral
  '#FF6347',  // Tomato red
  
  // Professional Cool Spectrum (contrast balance)
  '#4A90E2',  // Professional blue
  '#5DADE2',  // Sky blue  
  '#48C9B0',  // Teal green
  
  // Sophisticated Accent Spectrum
  '#AF7AC5',  // Soft purple
  '#F1C40F',  // Golden yellow
  '#E67E22'   // Burnt orange
];

// Get unique color for each pool based on its configuration
export const getPoolColor = (poolConfig: PoolConfig): string => {
  const dexIndex = ['uniswap_v3', 'pancakeswap_v3', 'uniswap_v4', 'sushiswap_v3'].indexOf(poolConfig.dex);
  const feeTier = poolConfig.feeTier || 0.3; // Default to 0.3% if undefined
  
  // Base color index for the DEX
  const baseColorIndex = dexIndex * 3;
  
  // Determine color variation based on fee tier and token pair
  let colorVariation = 0;
  if (poolConfig.name.includes('WBTC')) {
    colorVariation = 0; // Base color
  } else if (poolConfig.name.includes('ETH')) {
    colorVariation = 1; // Lighter variant
  } else if (poolConfig.name.includes('USDT')) {
    colorVariation = 2; // Darker variant
  } else if (poolConfig.name.includes('USDC')) {
    colorVariation = 1; // Lighter variant for USDC
  } else {
    // Fallback based on fee tier
    colorVariation = feeTier <= 0.3 ? 0 : feeTier <= 0.5 ? 1 : 2;
  }
  
  return POOL_COLOR_PALETTE[baseColorIndex + colorVariation];
};
